import React, { useState, useLayoutEffect, useEffect } from 'react';
import {
  Card,
  CloseButton,
  IconButton,
  IconArrowOpenUpLine,
  IconArrowOpenDownLine,
} from '@smarterservices/smarter-ui';
import {
  getAttachment,
  collapseIframe,
  expandIframe,
  removeAttachmentItem,
} from '../../../helpers/helpers';
import { ParamsNotFoundError } from '../../../helpers/definedErrors';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const useWindowHeight = () => {
  const [windowHeight, setWindowHeight] = useState(0);

  useLayoutEffect(() => {
    // Function to update the height state
    const updateHeight = () => {
      setWindowHeight(window.innerHeight);
    };

    // Set initial height
    updateHeight();

    // Add event listener for window resize
    window.addEventListener('resize', updateHeight);

    // Clean up event listener on unmount
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  return windowHeight;
};

const AttachmentItem = () => {
  const [title, setTitle] = useState(null);
  const [attachmentUrl, setAttachmentUrl] = useState(null);
  const [attachmentSid, setAttachmentSid] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const windowHeight = useWindowHeight();
  const renderToolbar = (Toolbar) => (
    <Toolbar>
      {(slots) => {
        const {
          CurrentPageInput,
          NumberOfPages,
          GoToFirstPage,
          GoToLastPage,
          GoToPreviousPage,
          GoToNextPage,
          ZoomIn,
          ZoomOut,
          Zoom,
          Rotate,
          ShowSearchPopover,
          SwitchTheme,
        } = slots;
        return (
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <div style={{ padding: '0 2px' }}>
              <ShowSearchPopover />
            </div>
            <div style={{ padding: '0 2px' }}>
              <GoToFirstPage />
            </div>
            <div style={{ padding: '0 2px' }}>
              <GoToPreviousPage />
            </div>
            <div style={{ padding: '0 2px', width: '4rem' }}>
              <CurrentPageInput />
            </div>
            <div style={{ padding: '0 2px' }}>
              / <NumberOfPages />
            </div>
            <div style={{ padding: '0 2px' }}>
              <GoToNextPage />
            </div>
            <div style={{ padding: '0 2px' }}>
              <GoToLastPage />
            </div>
            <div style={{ padding: '0 2px', marginLeft: 'auto' }}>
              <ZoomOut />
            </div>
            <div style={{ padding: '0 2px' }}>
              <Zoom />
            </div>
            <div style={{ padding: '0 2px' }}>
              <ZoomIn />
            </div>
            <div style={{ padding: '0 2px', marginLeft: 'auto' }}>
              <Rotate />
            </div>
            <div style={{ padding: '0 2px' }}>
              <SwitchTheme />
            </div>
          </div>
        );
      }}
    </Toolbar>
  );
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    toolbarPlugin: {
      fullScreenPlugin: {
        enableShortcuts: false,
      },
      getFilePlugin: {
        fileNameGenerator: () => 'document.pdf',
      },
      openPlugin: {
        enableShortcuts: false,
      },
      printPlugin: {
        enableShortcuts: false,
      },
    },
    sidebarTabs: () => [],
    renderToolbar,
  });

  useEffect(() => {
    const initAttachment = async () => {
      const url = new URL(window.location.href);
      const attachmentParams = new Proxy(new URLSearchParams(url.search), {
        get: (searchParams, prop) => searchParams.get(prop),
      });
      if (
        !attachmentParams ||
        !attachmentParams.attachmentSid ||
        !attachmentParams.token ||
        !attachmentParams.sessionSid ||
        !attachmentParams.examSid ||
        !attachmentParams.installSid ||
        !attachmentParams.title
      ) {
        throw new ParamsNotFoundError();
      }
      const response = await getAttachment(
        attachmentParams.attachmentSid,
        attachmentParams
      );
      setAttachmentSid(attachmentParams.attachmentSid);
      setAttachmentUrl(response);
      setTitle(attachmentParams.title);

      // Set expanded state and collapse iframe if needed
      const isExpanded = attachmentParams.isExpanded === 'true';
      setIsExpanded(isExpanded);

      if (!isExpanded) {
        collapseIframe(attachmentParams.attachmentSid);
      }
    };
    initAttachment();
  }, []);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
    if (isExpanded) {
      collapseIframe(attachmentSid);
    } else {
      expandIframe(attachmentSid);
    }
  };

  window.addEventListener('message', (event) => {
    if (event.data.type === 'sp_iframe_expand_state_change') {
      const isExpanded = event.data.isExpanded;
      setIsExpanded(isExpanded);
    }
  });

  const HEADER_HEIGHT = 50;
  const bodyHeight =
    windowHeight > 0 ? `${windowHeight - HEADER_HEIGHT}px` : '100vh';
  const workerUrl = new URL(
    'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js'
    //import.meta.url
  ).toString();

  return attachmentUrl && attachmentSid && title ? (
    <Card
      background="secondary"
      borderColor="brand"
      borderWidth="large"
      borderRadius="x-small"
    >
      <Card.Header background="brand">
        <Card.Title>
          {title.length > 17 ? title.substring(0, 17) + '...' : title}
        </Card.Title>
        <Card.Actions>
          {isExpanded ? (
            <IconButton
              withBackground={false}
              withBorder={false}
              margin="small"
              size="small"
              color="primary-inverse"
              screenReaderLabel="Collapse"
              focusColor="inverse"
              onClick={toggleExpand}
            >
              <IconArrowOpenUpLine />
            </IconButton>
          ) : (
            <IconButton
              withBackground={false}
              withBorder={false}
              margin="small"
              size="small"
              color="primary-inverse"
              screenReaderLabel="Expand"
              focusColor="inverse"
              onClick={toggleExpand}
            >
              <IconArrowOpenDownLine />
            </IconButton>
          )}
          <CloseButton
            color="primary-inverse"
            screenReaderLabel="Close"
            onClick={() => removeAttachmentItem(attachmentSid)}
          />
        </Card.Actions>
      </Card.Header>
      {isExpanded && (
        <Card.Body height={bodyHeight} padding="0">
          <Worker workerUrl={workerUrl}>
            <Viewer
              fileUrl={attachmentUrl}
              plugins={[defaultLayoutPluginInstance]}
            />
          </Worker>
        </Card.Body>
      )}
    </Card>
  ) : null;
};

export default AttachmentItem;
